import icons from '../icons';
import type { CssVariables, Features, ProductItem, ProductCategory, ProductUrls } from '../types';

/**
 * Creates an HTML string for a navigation item.
 *
 * @param item - The product item to create the navigation item for.
 * @param selectedMenuItem - The label of the currently selected menu item.
 * @returns The HTML string representing the navigation item.
 */
export const createItem = (item: ProductItem, selectedMenuItem?: string): string => {
  if (item.disabled) {
    return `
      <div class="uni-nav__category-item-wrapper--disabled">
        <div class="uni-nav__category-item uni-nav__category-item--disabled">
          <div class="uni-nav__category-item-icon">
            ${icons[item.icon]}
          </div>
          <div class="uni-nav__category-item-label">${item.label}</div>
          <div class="uni-nav__category-item-info-container">
            <a href="${item.url ?? '#'}" data-product="${item.label}" target="_blank" rel="noopener noreferrer" class="uni-nav__category-item-info-btn">
              ${icons.lucideInfo}
            </a>
          </div>
        </div>
      </div>
    `;
  }

  const isNewButton = `
    <div class="uni-nav__category-item-badge-container">
      <div class="uni-nav__category-item-badge">new</div>
    </div>
  `;
  return `
    <a href="${item.url ?? '#'}" data-product="${item.label}">
      <div class="uni-nav__category-item ${item.label === selectedMenuItem ? 'uni-nav__category-item--selected' : ''}">
        <div class="uni-nav__category-item-icon">
          ${icons[item.icon]}
        </div>
        <div>${item.label}</div>
        ${item.isNew ? isNewButton : ''}
      </div>
    </a>
  `;
};

/**
 * Creates an HTML string representing a category with its items.
 *
 * @param category - An object containing:
 *   - category: The name of the category
 *   - items: An array of items belonging to the category
 * @param selectedMenuItem - An optional string representing the selected menu item.
 * @returns An HTML string representing the category and its items.
 */
export const createCategory = (
  category: { category: string; items: ProductItem[] },
  selectedMenuItem?: string,
): string => {
  // Return empty string if items array is empty
  if (category.items.length === 0) {
    return '';
  }

  return `
    <div class="uni-nav__category">
      <div class="uni-nav__category-header">${category.category}</div>
      <div class="uni-nav__category-items">
        ${category.items.map((item) => createItem(item, selectedMenuItem)).join('')}
      </div>
    </div>
  `;
};

/**
 * Sets CSS variables on the document's root element.
 *
 * This function takes an object of CSS variables, converts the keys from camelCase to kebab-case,
 * and sets them as CSS custom properties on the document's root element (:root).
 *
 * @param cssVariables - An object where the keys are CSS variable names in camelCase and the values are the corresponding CSS values.
 *
 * @example
 * ```typescript
 * setCssVariables({
 *   primaryColor: '#ff0000',
 *   secondaryColor: '#00ff00'
 * });
 * // This will set the CSS variables --primary-color and --secondary-color on the :root element.
 * ```
 */
export const setCssVariables = (cssVariables: CssVariables): void => {
  Object.entries(cssVariables).forEach(([key, value]) => {
    // convert from camal case to kebab
    const kebabKey = key.replace(/(?<lower>[a-z])(?<upper>[A-Z])/g, '$<lower>-$<upper>').toLowerCase();
    document.documentElement.style.setProperty(`--${kebabKey}`, String(value));
  });
};

/**
 * Updates the URLs of product items in the given menu categories based on the provided product URLs.
 *
 * @param productUrls - An object containing the mapping of product labels to their respective URLs.
 * @param menuItems - An array of product categories, each containing a list of items.
 * @returns An array of product categories with updated URLs for the items that match the labels in the productUrls.
 */
export const updateProductUrls = (productUrls: ProductUrls, menuItems: ProductCategory[]): ProductCategory[] => {
  return menuItems.map((category) => ({
    category: category.category,
    items: category.items.map((item) => {
      if (item.label in productUrls) {
        return { ...item, url: productUrls[item.label as keyof ProductUrls] };
      }
      return item;
    }),
  }));
};

/**
 * Marks product items as disabled based on the provided list of disabled product labels.
 */
export const updateDisabledProducts = (menuItems: ProductCategory[], disabledProducts: string[]): ProductCategory[] => {
  if (disabledProducts.length === 0) return menuItems;
  return menuItems.map((category) => ({
    category: category.category,
    items: category.items.map((item) => (disabledProducts.includes(item.label) ? { ...item, disabled: true } : item)),
  }));
};

/**
 * Updates the feature flags for a list of product categories by filtering
 * the items within each category based on the provided feature flags.
 *
 * @param menuItems - An array of product categories, where each category contains
 *                    a list of items to be filtered.
 * @param featureFlags - An object containing feature flag values that determine
 *                       whether specific items should be included or excluded.
 * @returns A new array of product categories with their items filtered based
 *          on the feature flags.
 *
 */
export const updateFeatureFlags = (menuItems: ProductCategory[], featureFlags: Features): ProductCategory[] => {
  return menuItems.map((category) => ({
    category: category.category,
    items: category.items.filter((item) => {
      switch (item.label) {
        case 'survey':
          return featureFlags.showSurvey;
        case 'digital':
          return featureFlags.showDigital;
        case 'reputation':
          return featureFlags.showReputation;
        case 'workflow':
          return featureFlags.showWorkflow;
        case 'dashboard':
          return featureFlags.showDashboards;
        case 'pulse':
          return featureFlags.showPulse;
        case 'listings':
          return featureFlags.showListings;
        default:
          return true; // Keep the item if it doesn't match any of the known labels
      }
    }),
  }));
};
